import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useTRPC } from "../lib/trpc";
import { TRUST_TIER_LABELS } from "../lib/trustTiers";
import { useTenantRole } from "../lib/useTenantRole";

const COMPUTED = "__computed";
const TIERS = ["untrusted", "trusted", "trusted_7d", "trusted_30d", "super_trusted"] as const;
/** What the trigger shows for each value; Base UI renders the raw value without this map. */
const ITEMS: Record<string, string> = {
  [COMPUTED]: "Computed tier",
  ...Object.fromEntries(TIERS.map((tier) => [tier, `Pin: ${TRUST_TIER_LABELS[tier]}`])),
};

/**
 * Set or clear the trust-tier override. The computed tier drives withdrawal limits until an
 * editor pins one here; "Computed" hands control back. Editors and admins only.
 */
export function TrustTierControl({
  endUserId,
  override,
}: {
  endUserId: string;
  override: string | null;
}) {
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const set = useMutation(
    trpc.users.setTrustTierOverride.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          result.override
            ? `Trust tier pinned to ${TRUST_TIER_LABELS[result.override] ?? result.override}`
            : `Override cleared; computed tier is ${TRUST_TIER_LABELS[result.tier] ?? result.tier}`,
        );
        void queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
      },
    }),
  );
  if (!can("editor")) return null;

  return (
    <Select
      value={override ?? COMPUTED}
      items={ITEMS}
      disabled={set.isPending}
      onValueChange={(value) =>
        set.mutate({
          endUserId,
          tier: value === COMPUTED ? null : (value as (typeof TIERS)[number]),
        })
      }
    >
      <SelectTrigger size="sm" className="w-44" aria-label="Trust tier override">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={COMPUTED}>Computed tier</SelectItem>
        {TIERS.map((tier) => (
          <SelectItem key={tier} value={tier}>
            Pin: {TRUST_TIER_LABELS[tier]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
